import React, { useCallback, useState } from 'react';
import Select from 'react-select';
import { Filter, X } from 'lucide-react';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import OptionField, { selectProps } from './OptionField';
import PeriodField from './PeriodField';
import NumberListField, { mergeNumbers } from './NumberListField';
import PlateField, { mergePlates } from './PlateField';
import {
  DEFAULT_FILTERS,
  normalizeFilters,
  setField,
  getField,
  validDay,
  REGIONS,
  UFS,
  LINKS,
} from './contract';
import './styles.css';

const boolOptions = [
  { value: 'BOTH', label: 'Ambos' },
  { value: true, label: 'Sim' },
  { value: false, label: 'Não' },
];
function DateField({ id, label, value, onChange, onPending, hint }) {
  const [day, setDay] = useState('');
  const [error, setError] = useState(null);
  const add = () => {
    if (!validDay(day)) {
      setError('Informe uma data válida.');
      return;
    }
    if (!value.includes(day)) onChange([...value, day]);
    setDay('');
    onPending('');
    setError(null);
  };
  return (
    <div className="tc-f-field" data-filter-field={id}>
      <label htmlFor={id}>{label}</label>
      <div className="tc-f-date">
        <input
          id={id}
          aria-describedby={`${id}-hint`}
          aria-invalid={!!error}
          type="date"
          value={day}
          onChange={(e) => {
            setDay(e.target.value);
            onPending(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button
          size="xs"
          variant="secondary"
          disabled={value.length >= 100}
          onClick={add}
          aria-label={`Adicionar data: ${label}`}
        >
          Adicionar
        </Button>
      </div>
      {value.length > 0 && (
        <ul className="tc-f-dates" aria-label={`Datas selecionadas: ${label}`}>
          {value.map((date) => (
            <li key={date}>
              {date.split('-').reverse().join('/')}
              <button
                type="button"
                aria-label={`Remover ${date.split('-').reverse().join('/')} de ${label}`}
                onClick={() => onChange(value.filter((v) => v !== date))}
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <small id={`${id}-hint`}>{hint} Adicione cada dia desejado.</small>
      {error && <span role="alert">{error}</span>}
    </div>
  );
}
export default function FilterModal({ context, onClose, onApplyFilters }) {
  const [draft, setDraft] = useState(() => normalizeFilters(context.query?.filters));
  const [cache, setCache] = useState(context.filterLabels || {});
  const [error, setError] = useState(null);
  const [pendingDates, setPendingDates] = useState({});
  const [pendingNumbers, setPendingNumbers] = useState({});
  const [pendingPlates, setPendingPlates] = useState('');
  const [clearKey, setClearKey] = useState(0);
  const remember = useCallback(
    (id, rows) =>
      setCache((previous) => {
        const next = { ...previous[id], ...Object.fromEntries(rows.map((o) => [o.value, o])) };
        return JSON.stringify(next) === JSON.stringify(previous[id] || {})
          ? previous
          : { ...previous, [id]: next };
      }),
    [],
  );
  const change = (path, values) => {
    try {
      setDraft(setField(draft, path, values));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };
  const field = (path, label, config = {}) => (
    <OptionField
      key={path}
      id={`tc-filter-${path}`}
      label={label}
      value={getField(draft, path)}
      onChange={(values) => change(path, values)}
      cache={cache[`tc-filter-${path}`]}
      remember={remember}
      {...config}
    />
  );
  const bool = (path, label) => {
    const values = getField(draft, path);
    const inputId = `tc-filter-${path}-${label.replace(/\W+/g, '-')}`;
    return (
      <div className="tc-f-field" data-filter-field={`tc-filter-${path}`}>
        <label htmlFor={inputId}>{label}</label>
        <Select
          {...selectProps}
          inputId={inputId}
          aria-label={label}
          options={boolOptions}
          value={
            values.length ? boolOptions.filter((o) => values.includes(o.value)) : [boolOptions[0]]
          }
          menuPortalTarget={document.body}
          onChange={(items, action) =>
            change(
              path,
              action.option?.value === 'BOTH'
                ? []
                : items.filter((o) => typeof o.value === 'boolean').map((o) => o.value),
            )
          }
        />
      </div>
    );
  };
  const period = (path, suffix, hint, aside) => (
    <PeriodField
      id={`tc-filter-${path}`}
      value={getField(draft, path)}
      onChange={(value) => change(path, value)}
      suffix={suffix}
      hint={hint}
      aside={aside}
    />
  );
  // 5713: sem placas a chave não existe no filtro normalizado.
  const plates = (
    <PlateField
      id="tc-filter-travels.plates"
      label="Placas"
      value={getField(draft, 'travels.plates') ?? []}
      onChange={(values) => change('travels.plates', values)}
      onPending={setPendingPlates}
    />
  );
  const numbers = (path, label) => (
    <NumberListField
      id={`tc-filter-${path}`}
      label={label}
      value={getField(draft, path)}
      onChange={(values) => change(path, values)}
      onPending={(text) => setPendingNumbers((previous) => ({ ...previous, [path]: text }))}
    />
  );
  // Cargas e Viagens têm os mesmos campos cadastrais; os catálogos de Viagens vêm das viagens abertas.
  const route = (section, config = {}) => [
    field(`${section}.costCenterIds`, 'CC', { catalog: 'costCenterIds', ...config }),
    field(`${section}.regions`, 'Regional', { options: REGIONS }),
    field(`${section}.clientIds`, 'Cliente (Razão Social / CNPJ)', {
      catalog: 'clientIds',
      ...config,
    }),
    field(`${section}.originCityIds`, 'Rota — Origem (Cidade/UF)', {
      catalog: 'originCityIds',
      ...config,
    }),
    field(`${section}.destinationCityIds`, 'Rota — Destino (Cidade/UF)', {
      catalog: 'destinationCityIds',
      ...config,
    }),
    field(`${section}.productIds`, 'Produtos', { catalog: 'productIds', ...config }),
  ];
  const dates = (path, label, hint) => (
    <DateField
      id={`tc-filter-${path}`}
      label={label}
      value={getField(draft, path)}
      onChange={(values) => change(path, values)}
      onPending={(day) => setPendingDates((previous) => ({ ...previous, [path]: day }))}
      hint={hint}
    />
  );
  return (
    <Modal
      show
      centered
      scrollable
      className="tc-f-modal"
      dialogClassName="tc-f-dialog"
      handleClose={onClose}
      aria-labelledby="tc-f-title"
      heading={
        <span id="tc-f-title">
          <Filter size={19} aria-hidden="true" /> Filtros
        </span>
      }
      body={
        <div className="tc-f-body" key={clearKey}>
          <p className="tc-f-intro">
            Combine opções para consultar as cargas e viagens. As alterações só entram em vigor ao
            aplicar.
          </p>
          <div className="tc-f-columns">
            <section aria-labelledby="tc-f-loads">
              <h3 id="tc-f-loads">Cargas</h3>
              <div className="tc-f-grid tc-f-grid-two">
                {period('loads.period', 'coleta', 'Pré Cargas / Cargas usam a data de coleta.')}
                {numbers('loads.loadIds', 'Nº carga')}
                {route('loads')}
                {bool('loads.moppRequired', 'MOPP exigido pela carga')}
              </div>
              <label className="tc-f-check" htmlFor="tc-filter-loads.withAvailableVehicles">
                <input
                  id="tc-filter-loads.withAvailableVehicles"
                  type="checkbox"
                  checked={getField(draft, 'loads.withAvailableVehicles') === true}
                  onChange={(event) =>
                    change('loads.withAvailableVehicles', event.target.checked || undefined)
                  }
                />
                <span>
                  Apenas cargas com veículos disponíveis
                  <small>Mostra só as cargas com ao menos um veículo no botão Veículos.</small>
                </span>
              </label>
            </section>
            <section aria-labelledby="tc-f-travels">
              <h3 id="tc-f-travels">Viagens</h3>
              <div className="tc-f-grid tc-f-grid-two">
                {period(
                  'travels.period',
                  '',
                  'Atendidas usam a data de coleta; Em viagem e No destino, a data de entrega.',
                  plates,
                )}
                {numbers('travels.travelIds', 'Nº viagem')}
                {route('travels', { scope: 'travels' })}
                {bool('travels.moppRequired', 'MOPP exigido pela carga')}
              </div>
            </section>
          </div>
          <section aria-labelledby="tc-f-vehicles">
            <h3 id="tc-f-vehicles">Veículos</h3>
            <div className="tc-f-grid">
              {field('candidates.linkTypes', 'Vínculo', { options: LINKS })}
              {field('loads.vehicleTypeIds', 'Tipo de veículo', { catalog: 'vehicleTypeIds' })}
              {field('loads.bodyTypeIds', 'Tipo de carroceria', { catalog: 'bodyTypeIds' })}
              {bool('loads.trackingRequired', 'Veículos rastreados')}
              {field('loads.riskManagerIds', 'Gerenciadora de risco', {
                catalog: 'riskManagerIds',
              })}
              {bool('loads.moppRequired', 'MOPP exigido (mesma seleção de Cargas)')}
            </div>
          </section>
          <section aria-labelledby="tc-f-empty">
            <h3 id="tc-f-empty">Veículos vazios</h3>
            <div className="tc-f-grid tc-f-grid-four">
              {field('candidates.empty.regions', 'Região do veículo vazio', { options: REGIONS })}
              {field('candidates.empty.provinceUfs', 'Estado do veículo vazio', { options: UFS })}
              {field('candidates.empty.cityIds', 'Cidade do veículo vazio', { catalog: 'cities' })}
              {bool('candidates.empty.driverHasMopp', 'MOPP do motorista vazio')}
            </div>
          </section>
          <section aria-labelledby="tc-f-transit">
            <h3 id="tc-f-transit">Veículos em viagem</h3>
            <div className="tc-f-grid">
              {field('candidates.inTransit.regions', 'Chegando à região', { options: REGIONS })}
              {field('candidates.inTransit.provinceUfs', 'Chegando ao estado', { options: UFS })}
              {field('candidates.inTransit.cityIds', 'Chegando à cidade', { catalog: 'cities' })}
              {dates(
                'candidates.inTransit.estimatedDeliveryDates',
                'Previsão de entrega',
                'Dia da previsão calculada de chegada à entrega final, em Brasília.',
              )}
              {dates(
                'candidates.inTransit.scheduledDeliveryDates',
                'Data da entrega',
                'Dia agendado da última entrega da viagem; não é a previsão calculada.',
              )}
              {bool('candidates.inTransit.driverHasMopp', 'MOPP do motorista em viagem')}
            </div>
          </section>
          {error && (
            <p role="alert" className="tc-f-error">
              {error}
            </p>
          )}
        </div>
      }
      footer={
        <div className="tc-f-footer">
          <Button
            variant="secondary"
            onClick={() => {
              setDraft(DEFAULT_FILTERS);
              setPendingDates({});
              setPendingNumbers({});
              setPendingPlates('');
              setClearKey((value) => value + 1);
              setError(null);
            }}
          >
            Limpar
          </Button>
          <div>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={() => {
                try {
                  let applied = normalizeFilters(draft);
                  for (const [path, day] of Object.entries(pendingDates)) {
                    if (day) applied = setField(applied, path, [...getField(applied, path), day]);
                  }
                  // Número digitado e ainda não confirmado também entra ao aplicar.
                  for (const [path, text] of Object.entries(pendingNumbers)) {
                    if (text)
                      applied = setField(
                        applied,
                        path,
                        mergeNumbers(getField(applied, path), text).numbers,
                      );
                  }
                  if (pendingPlates)
                    applied = setField(
                      applied,
                      'travels.plates',
                      mergePlates(getField(applied, 'travels.plates') ?? [], pendingPlates).plates,
                    );
                  onApplyFilters(applied, cache);
                } catch (e) {
                  setError(e.message);
                }
              }}
            >
              Aplicar filtros
            </Button>
          </div>
        </div>
      }
    />
  );
}
